// hooks/useCreateNewReservation.ts
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store"; // Adjust the import based on your file structure
import { CreateNewReservationData } from "../../api/types";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { useCreateNewReservationMutation } from "../../api/reservations/reservationsApiSlice";
import { processReservationData } from "./processors";
import { reservationCreated } from "../slice/bookingCreation/bookingCreationSlice";
import { notifyBookingFailure } from "../helpers/failureNotification";
import { getTenantName, setTimeOnDate } from "../../util";
import { resolveRequestDates } from "./reservationWindow";
import { useSelectedLocationTimeZone } from "../../util/locationTimeZone";
import { selectRoomFamilyGroupId } from "./roomGroupId";
import { requestCarriesTimeOfDay } from "../../util/roomRequestShape";

// import { Reservation } from "../../types/bookingCreation";

export const useCreateNewReservation = (
  useCase?: string,
  options?: {
    throwOnError?: boolean;
    /**
     * Room-family flows only (BookingFlowShell): book the group
     * `selectRoomFamilyGroupId` names — the selected room's own group first,
     * `assetGroupId` only as fallback — the same rule
     * `createVenueReservationFlow` uses. Opt-in, because `selectedApartment`
     * is room state that can outlive its flow: the station flows sharing this
     * hook must never book a stale room's group.
     */
    roomFamilyGroup?: boolean;
  }
) => {

  const assetGroupSelection = useSelector(
    (state: RootState) => state.bookingCreation.assetGroupId
  );
  const roomFamilyGroupId = useSelector(selectRoomFamilyGroupId);
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  const [createNewReservationMutation] = useCreateNewReservationMutation();
  const dispatch = useDispatch();
  // The booked location's clock (#3219) — the server reads the window there.
  const timeZone = useSelectedLocationTimeZone();

  const groupId = options?.roomFamilyGroup
    ? roomFamilyGroupId
    : assetGroupSelection;

  const createNewReservation = async () => {
    try {
      if (!groupId || !orderData) {
        // Never a silent return: the caller would carry on as if a
        // reservation existed — the room flow then PATCHed and tried to pay a
        // reservation that was never created. Thrown into the catch below so
        // it is logged, tracked and told exactly like an API failure.
        throw new Error(
          `createNewReservation submitted without ${
            !groupId ? "a group id" : "order data"
          }`
        );
      }

      // The ONE window builder (reservationWindow.ts) — this flow used to
      // carry its own copy, which is how the room flow's dates went out as
      // the phone's midnight rather than the station's.
      let { fromDate, toDate } = resolveRequestDates(orderData, timeZone);

      // Wire 2 stage 2: venue direct booking books an hourly window — stamp
      // the picked start/end times exactly as the venue request flow does, so
      // the reservation the payment charges covers the same window a request
      // would have asked for.
      if (requestCarriesTimeOfDay(useCase)) {
        fromDate = setTimeOnDate(fromDate, orderData.startTime!, timeZone);
        toDate = setTimeOnDate(toDate, orderData.endTime!, timeZone);
      }

      const newReservation: Array<CreateNewReservationData> = [
        {
          groupId,
          amount: orderData.amount,
          fromDate: fromDate,
          toDate: toDate,
          isFullDay: false,
          quantity: orderData.quantity || 1,
          useCase: useCase ?? undefined,
        },
      ];

      // Call the mutation function
      const response = await createNewReservationMutation(
        newReservation
      ).unwrap();

      const processedReservation = processReservationData(response);
      if (processedReservation) {
        dispatch(reservationCreated({ reservation: processedReservation }));
      }

      return processedReservation;
    } catch (error) {
      const context = {
        useCase,
        groupId,
        fromDate: orderData?.checkInDate?.toISOString?.() ?? orderData?.date?.toISOString?.(),
        tenant: getTenantName(),
      };
      Logger.for("createNewReservation").error(error, context);
      ErrorTracker.trackException(error as Error, context);
      // Rethrow only when caller opts in. Default behavior remains a silent
      // `undefined` return so existing flows (roomRental, equipmentRental,
      // p2p, temporaryStorage/newBooking) keep their previous semantics.
      if (options?.throwOnError) {
        throw error;
      }
      // Only the swallowing path notifies (#1970). A caller that opted into
      // the rethrow handles the failure itself — sup renders a persistent
      // inline error with a retry in ConfirmationStep — and a toast on top of
      // that would say the same thing twice.
      notifyBookingFailure(dispatch, error);
      return undefined;
    }
  };

  return createNewReservation;
};
